import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationTemplateApi } from '@/services/notificationApi';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Mail, BarChart3 } from 'lucide-react';
import type { NotificationChannel } from '@/types/notification';

export const NotificationTemplateStatsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    inactive: 0,
    archived: 0,
    byChannel: {} as Record<NotificationChannel, number>,
    byLocale: {} as Record<string, number>,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      // Fetch all templates (with a large page size to get all)
      const response = await notificationTemplateApi.list({
        page: 0,
        size: 1000, // Large size to get all templates
      });

      const templates = response.content || [];

      // Calculate statistics
      const newStats = {
        total: templates.length,
        active: templates.filter((t) => t.status === 'ACTIVE').length,
        draft: templates.filter((t) => t.status === 'DRAFT').length,
        inactive: templates.filter((t) => t.status === 'INACTIVE').length,
        archived: templates.filter((t) => t.status === 'ARCHIVED').length,
        byChannel: {} as Record<NotificationChannel, number>,
        byLocale: {} as Record<string, number>,
      };

      // Count by channel
      templates.forEach((template) => {
        newStats.byChannel[template.channel] = (newStats.byChannel[template.channel] || 0) + 1;
      });

      // Count by locale
      templates.forEach((template) => {
        newStats.byLocale[template.locale] = (newStats.byLocale[template.locale] || 0) + 1;
      });

      setStats(newStats);
    } catch (error: unknown) {
      console.error('Error fetching stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load statistics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getChannelIcon = (channel: NotificationChannel) => {
    switch (channel) {
      case 'EMAIL':
        return '📧';
      case 'SMS':
        return '💬';
      case 'WHATSAPP':
        return '💚';
      case 'PUSH_NOTIFICATION':
        return '🔔';
      case 'IN_APP':
        return '📱';
      default:
        return '📄';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/notifications/templates">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Template Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Overview of notification templates</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
                <Mail className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">All templates</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active</CardTitle>
                <Mail className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Draft</CardTitle>
                <Mail className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
                <p className="text-xs text-muted-foreground">In draft</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Inactive</CardTitle>
                <Mail className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
                <p className="text-xs text-muted-foreground">Inactive</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Archived</CardTitle>
                <Mail className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.archived}</div>
                <p className="text-xs text-muted-foreground">Archived</p>
              </CardContent>
            </Card>
          </div>

          {/* Channel Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Channel</CardTitle>
              <CardDescription>Number of templates per notification channel</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byChannel).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byChannel)
                    .sort(([, a], [, b]) => b - a)
                    .map(([channel, count]) => (
                      <div key={channel} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{getChannelIcon(channel as NotificationChannel)}</span>
                          <span className="font-medium">{channel.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No templates found</div>
              )}
            </CardContent>
          </Card>

          {/* Locale Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Locale</CardTitle>
              <CardDescription>Number of templates per locale</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byLocale).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byLocale)
                    .sort(([, a], [, b]) => b - a)
                    .map(([locale, count]) => (
                      <div key={locale} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium uppercase">{locale}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No templates found</div>
              )}
            </CardContent>
          </Card>

          {/* Usage Statistics Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Statistics</CardTitle>
              <CardDescription>Template usage metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Usage statistics will be available when backend metrics are enabled.</p>
                <p className="text-sm mt-2">
                  This feature requires backend metrics collection to be configured.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default NotificationTemplateStatsPage;








